import { useTranslation, untranslated } from "@i18n";

import { forwardRef, useEffect, useRef, useState, type ComponentProps } from "react";
import { useDispatch } from "react-redux";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { mileStoneServiceUrl } from "@Features/contracts/api/contracts.api";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import DateTimePickerComponent from "@CubeComponents/Mui_Form/DateTimeComponent";
import { getContractDetails, mileStoneController } from "@Features/contracts/state/contracts.actions";
import { convertDate } from "@Services/luxonHelper";
import { BiLinkExternal } from "react-icons/bi";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import { ContractTaskGrid as ContractTasks } from "@Features/forms";
import {
  DisabledIllustration,
  getContractCRUDPermission,
  mileStonStatusTemplate,
} from "@Features/commercial-shared/contracts/contractHelpers";
import { TextField } from "@mui/material";
import { Formatters } from "slickgrid-react";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import SlickGrid from "@CubeComponents/html/SlickGrid1";
import type { ContractDetailsState } from "@Features/contracts/state/contracts.reducer";
import type { GetMileStoneControllerResponse } from "@ServerTypes/contracts/contracts.schema";

/** A single milestone row as returned by the milestone controller list response. */
type MileStoneRow = NonNullable<GetMileStoneControllerResponse["result"]>[number];

/** Date sub-object stored on the milestone create/edit form state. */
interface MileStoneFormDate {
  timeStamp?: number | null;
  basedOnTask?: boolean;
  taskId?: string;
  taskTitle?: string;
  offSet?: number;
  dateType?: string | null;
  selectedDate?: number | null;
}

/** Local form state for the create / edit milestone modal. */
interface MileStoneFormData {
  title?: string;
  description?: string;
  mileStoneAmount?: number | string;
  percentageOfTotal?: number | string;
  contractId?: string;
  projectId?: string;
  parentId?: string | null;
  hasChild?: boolean;
  revisedAmount?: number;
  revisedPercentageOfTotal?: number;
  dateType?: string;
  mileStoneDate?: MileStoneFormDate | null;
}

/** State backing the "select task" modal. `selectedTask` is a schedule task row. */
interface ShowTaskListState {
  status: boolean;
  selectedTask: {
    _id?: string;
    title?: string;
    startDate?: number | null;
    endDate?: number | null;
  } | null;
}

interface MileStonesProps {
  contractId: string;
  projectId: string;
  showOnlyGrid?: boolean;
  setBtnState?: (state: boolean) => void;
  formId?: string;
  changeOrderState?: unknown;
  contractDetails: ContractDetailsState;
}

const MileStones = forwardRef(
  (
    {
      contractId,
      projectId,
      showOnlyGrid = false,
      setBtnState,
      formId,
      changeOrderState,
      contractDetails,
    }: MileStonesProps,
    ref: any,
  ) => {
    const initialData: MileStoneFormData = {
      title: "",
      description: "",
      mileStoneAmount: "",
      contractId,
      projectId,
      mileStoneDate: {},
    };
    const [mileStoneData, setMileStoneData] = useState<MileStoneFormData>(initialData);
    const dispatch = useDispatch();
    const localRef = useRef<any>(null);
    const treeGrid = ref || localRef;
    const taskGridRef = useRef<any>(null);
    const [showAddMileStoneModal, setShowAddMileStoneModal] = useState({
      status: false,
      isChildContract: false,
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [toggleStatus, setToggleStatus] = useState(false);
    const [showTaskList, setShowTaskList] = useState<ShowTaskListState>({
      status: false,
      selectedTask: null,
    });
    const [disabledRows, setDisabledRows] = useState([]);
    const [toolBarBtnState, setToolBarBtnState] = useState({
      add: true,
      addChild: false,
      edit: false,
      delete: false,
    });
    const { t } = useTranslation();

    const isModuleDisabled = !contractDetails.modulesEnabled?.milestones;

    const [mileStoneQuery, setMileStoneQuery] = useState({
      formId,
      contractId,
      contractVersionId: contractDetails.versionId,
      params: {
        formId,
        contractId,
        contractVersionId: contractDetails.versionId,
      },
    });

    useEffect(() => {
      setMileStoneQuery({
        formId,
        contractId,
        contractVersionId: contractDetails.versionId,
        params: {
          formId,
          contractId,
          contractVersionId: contractDetails.versionId,
        },
      });
    }, [contractDetails.versionId, contractId, formId]);

    const textFieldCommonField = {
      width: "100%",
      "& .MuiOutlinedInput-input": {
        padding: "0.5rem",
      },
      "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
          borderColor: "var(--info-light-blue)",
          borderWidth: "0.0625rem",
        },
      },
    };

    const roundOffTemplate = (value?: number) => {
      return Number.parseFloat((value ?? 0).toFixed(2));
    };

    const handleInputChange = (
      args: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldType: "title" | "description" | "mileStoneAmount" | "percentageOfTotal",
    ) => {
      if (fieldType == "mileStoneAmount") {
        const value = Number(args.target.value);
        const percentageOfTotal = contractDetails.budgetAmount
          ? ((value * 100) / contractDetails.budgetAmount).toFixed(2)
          : 0;
        setMileStoneData((prev) => ({
          ...prev,
          percentageOfTotal: Number(percentageOfTotal),
          [fieldType]: value,
        }));
      } else if (fieldType == "percentageOfTotal") {
        const value = Number(args.target.value);
        if (value > 100) {
          return;
        }
        const mileStoneAmount = (contractDetails.budgetAmount! * value) / 100;
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneAmount: Number(mileStoneAmount),
          [fieldType]: value,
        }));
      } else {
        setMileStoneData((prev) => ({
          ...prev,
          [fieldType]: args.target.value,
        }));
      }
    };

    const handleChange = (args: any, fieldType: "basedOnTask" | "timeStamp" | "dateType" | "offSet") => {
      if (fieldType === "basedOnTask") {
        setToggleStatus(args.target.checked);
        if (args.target.checked) {
          setMileStoneData((prev) => ({
            ...prev,
            mileStoneDate: {
              ...prev.mileStoneDate,
              [fieldType]: true,
              timeStamp: null,
              dateType: "startDate",
            },
          }));
        } else {
          setMileStoneData((prev) => ({
            ...prev,
            mileStoneDate: {
              ...prev.mileStoneDate,
              timeStamp: null,
              basedOnTask: false,
              dateType: null,
            },
          }));
          setShowTaskList({ status: false, selectedTask: null });
        }
      } else if (fieldType === "timeStamp") {
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            timeStamp: new Date(args.value).getTime(),
            basedOnTask: false,
          },
        }));
      } else if (fieldType === "dateType") {
        let timeStamp;
        if (args.value === "startDate") {
          timeStamp = showTaskList.selectedTask?.startDate ?? null;
        } else {
          timeStamp = showTaskList.selectedTask?.endDate ?? null;
        }
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            ...prev.mileStoneDate,
            dateType: args.value,
            timeStamp,
            selectedDate: timeStamp,
            offSet: 0,
          },
        }));
      } else if (fieldType === "offSet" && args.previousValue != args.value) {
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            ...prev.mileStoneDate,
            offSet: args.value,
            timeStamp: (prev.mileStoneDate!.selectedDate ?? 0) + args.value * 24 * 60 * 60 * 1000,
          },
        }));
      }
    };

    const handleOpenTaskList = () => {
      setShowTaskList((prev) => ({ ...prev, status: true }));
    };

    const mileStoneDateMapper = [
      {
        heading: t("Based on Activity"),
        isVisible: true,
        component: (
          <ToggleSwitchComponent
            checked={toggleStatus || mileStoneData.mileStoneDate?.basedOnTask}
            {...({ name: "basedOnTask" } as any)}
            onChange={(args: any) => handleChange(args, "basedOnTask")}
          />
        ),
      },
      {
        heading: t("Milestone Date"),
        isVisible: !toggleStatus,
        component: (
          <DateTimePickerComponent
            onChange={(args: any) => handleChange(args, "timeStamp")}
            value={
              mileStoneData.mileStoneDate?.timeStamp ? new Date(mileStoneData.mileStoneDate?.timeStamp) : undefined
            }
            name="milestoneDate"
            width="80%"
            id="milestoneDate"
            placeholder={t("Select Date")}
            format="dd/MM/yyyy hh:mm"
          />
        ),
      },
      {
        heading: t("Select Activity"),
        isVisible: toggleStatus,
        component: (
          <div className="flex gap-3 items-center">
            <BiLinkExternal onClick={handleOpenTaskList} className="cursor-pointer" />
            <span>{showTaskList.selectedTask?.title || mileStoneData?.mileStoneDate?.taskTitle}</span>
          </div>
        ),
      },
      {
        heading: t("Based on Activity's Date"),
        isVisible: toggleStatus,
        component: (
          <SelectDropDown
            onChange={(e: any) => handleChange({ value: e.value }, "dateType")}
            value={mileStoneData.mileStoneDate?.dateType || "startDate"}
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            fields={{
              text: "name",
              value: "value",
            }}
            options={[
              { name: t("Start Date"), value: "startDate" },
              { name: t("End Date"), value: "endDate" },
            ]}
            buttonWidth="400"
            popperWidth={300}
          />
        ),
      },
      {
        heading: t("Offset (Days)"),
        isVisible: toggleStatus,
        component: (
          <NumericTextBoxComponent
            width="80%"
            value={mileStoneData.mileStoneDate?.offSet}
            name="offSet"
            placeholder={t("Select Offset")}
            change={(args) => handleChange(args, "offSet")}
          />
        ),
      },
      {
        heading: t("Date"),
        isVisible: toggleStatus,
        component: mileStoneData?.mileStoneDate?.timeStamp
          ? convertDate(Number(mileStoneData?.mileStoneDate?.timeStamp), "dateForTooltip")
          : "",
      },
    ];

    const mileStoneParametersMapper = [
      {
        id: "title",
        heading: `${t("Title")}*`,
        component: (
          <TextFieldComponent
            showClearButton
            value={mileStoneData.title}
            placeholder={t("Enter title")}
            name="title"
            onChange={(args: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleInputChange(args, "title")
            }
          />
        ),
      },
      {
        id: "description",
        heading: `${t("Description")}*`,
        component: (
          <TextFieldComponent
            value={mileStoneData.description}
            showClearButton
            multiline
            placeholder={t("Enter description")}
            name="description"
            onChange={(args: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleInputChange(args, "description")
            }
          />
        ),
      },
      {
        id: "amount",
        heading: t("Amount"),
        component: (
          <TextField
            onChange={(args) => handleInputChange(args, "mileStoneAmount")}
            disabled={mileStoneData.hasChild && editMode}
            value={mileStoneData?.mileStoneAmount}
            placeholder={t("Enter amount")}
            name="mileStoneAmount"
            inputProps={{
              min: 0,
              max: contractDetails.remainingBudgetAmountForMileStones! + (editMode ? mileStoneData.revisedAmount! : 0),
            }}
            sx={textFieldCommonField}
            type="number"
            focused
          />
        ),
      },
      {
        id: "%OfTotal",
        heading: t("% of Total"),
        component: (
          <TextField
            onChange={(args) => handleInputChange(args, "percentageOfTotal")}
            value={mileStoneData.percentageOfTotal}
            name="amount"
            inputProps={{
              min: 0,
              max:
                (contractDetails.remainingBudgetAmountForMileStones! * 100) / contractDetails.budgetAmount! +
                (editMode ? mileStoneData.revisedPercentageOfTotal! : 0),
            }}
            placeholder={t("Enter %")}
            disabled={mileStoneData.hasChild && editMode}
            sx={textFieldCommonField}
            type="number"
            focused
          />
        ),
      },
      {
        id: "MileStoneDate",
        heading: t("Mile Stone Date"),
        component: (
          <div className="flex w-full flex-col gap-5 p-2 items-center">
            {mileStoneDateMapper.map(
              (field, index) =>
                field.isVisible && (
                  <span key={index} className="w-full flex">
                    <span className="flex flex-1">{field.heading}</span>
                    <span className="flex flex-1">{field.component}</span>
                  </span>
                ),
            )}
          </div>
        ),
      },
    ];

    const handleAddMileStoneModalShow = () => {
      setShowAddMileStoneModal((prev) => {
        if (prev.status) {
          setMileStoneData(initialData);
          setEditMode(false);
          setToggleStatus(false);
          closeTaskListModal();
        }
        return {
          status: !prev.status,
          isChildContract: false,
        };
      });
    };

    const showHideDeleteModal = () => {
      setShowDeleteModal((prev) => !prev);
    };

    const toolBarItems = [
      {
        name: t("Create"),
        prefixIcon: "add",
        id: "create",
        disabled: !getContractCRUDPermission(contractDetails, changeOrderState),
      },
      {
        name: t("Create Child"),
        prefixIcon: "add",
        id: "create_child",
        disabled: !toolBarBtnState.addChild,
      },
      {
        name: t("Edit"),
        prefixIcon: "editProfile",
        id: "edit",
        disabled: !toolBarBtnState.edit,
      },
      {
        name: t("Delete"),
        prefixIcon: "delete",
        id: "delete",
        disabled: !toolBarBtnState.delete,
      },
      {
        name: t("ExpandAll"),
        prefixIcon: "Expand",
        id: "expandAll",
      },
      {
        name: t("CollapseAll"),
        prefixIcon: "Collapse",
        id: "collapseAll",
      },
    ];

    const createMileStoneClick = async () => {
      const data: any = {
        action: "insert",
        data: {
          ...mileStoneData,
        },
      };
      if (editMode) {
        data.action = "update";
        delete data.data.contractId;
        delete data.data.percentageOfTotal;
        delete data.data.projectId;
        data._id = treeGrid.current.dataView.getAllSelectedItems()[0]._id;
        data.contractId = contractId;
      }
      const response = await mileStoneController(data);
      if (response.data.status) {
        const content = editMode
          ? t("Milestone {{title}} updated successfully", { title: mileStoneData.title })
          : t("Milestone {{title}} created successfully", { title: mileStoneData.title });
        dispatch(
          showToastNotification({
            content,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        treeGrid.current.refresh();
        setMileStoneData(initialData);
        setEditMode(false);
        setToggleStatus(false);
        handleAddMileStoneModalShow();
        dispatch(getContractDetails({ contractId }));
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(response.data.errorMessage),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    };

    const toolbarClickHandler = async (args: string) => {
      const selectedData: MileStoneRow = treeGrid.current.dataView.getAllSelectedItems()[0];

      if (args === "create") {
        handleAddMileStoneModalShow();
      }

      if (args === "create_child") {
        setMileStoneData((prev) => ({
          ...prev,
          parentId: selectedData?.mileStoneId,
        }));
        setShowAddMileStoneModal(() => ({
          status: true,
          isChildContract: true,
        }));
      }
      if (args === "edit") {
        setShowAddMileStoneModal((prev) => ({
          status: !prev.status,
          isChildContract: false,
        }));
        setEditMode(true);
        setMileStoneData({
          title: selectedData.title,
          hasChild: selectedData.hasChild,
          description: selectedData.description,
          mileStoneAmount: selectedData.revisedAmount,
          percentageOfTotal: selectedData.revisedPercentageOfTotal,
          mileStoneDate: selectedData.mileStoneDate,
          parentId: selectedData.parentId,
        });
        if (selectedData.mileStoneDate?.basedOnTask) {
          setToggleStatus(true);
        }
      }
      if (args === "delete") {
        showHideDeleteModal();
      } else if (args === "collapseAll") {
        treeGrid.current?.treeDataService.toggleTreeDataCollapse(true);
      } else if (args === "expandAll") {
        treeGrid.current?.treeDataService.toggleTreeDataCollapse(false);
      }
    };

    const selectionHandler = () => {
      const selectedData = treeGrid.current.dataView.getAllSelectedItems();

      if (setBtnState) {
        if (selectedData.length > 0) {
          let btnState = true;
          selectedData?.map((item: MileStoneRow) => {
            if (item?.isAlreadyAddedInThisForm) {
              btnState = false;
            }
          });
          setBtnState(btnState);
        } else {
          setBtnState(false);
        }
      }
      if (showOnlyGrid || !getContractCRUDPermission(contractDetails, changeOrderState)) {
        return;
      }
      const totalRowsSelected = selectedData.length;
      setToolBarBtnState((prev) => ({
        ...prev,
        addChild: totalRowsSelected == 1,
        edit: totalRowsSelected == 1,
        delete: totalRowsSelected >= 1,
      }));
    };

    const deleteMileStone = async () => {
      const selectedMileStones = treeGrid.current.dataView.getAllSelectedItems();
      const mileStoneIds = selectedMileStones.map((mileStone: MileStoneRow) => mileStone.mileStoneId);
      const data = {
        action: "delete",
        mileStoneIds,
        contractId,
      };
      let isAnyMileStoneAddedInForm = false;
      selectedMileStones.map((milestone: MileStoneRow) => {
        if (milestone.isAddedInForms) {
          isAnyMileStoneAddedInForm = true;
        }
      });
      if (isAnyMileStoneAddedInForm) {
        dispatch(
          showToastNotification({
            content: t(
              "One or more of the selected milestone(s) is already added in forms and hence cannot be removed",
            ),
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        const res = await mileStoneController(data);
        if (res.data.status) {
          dispatch(
            showToastNotification({
              content: t("Selected milestone(s) deleted successfully"),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          showHideDeleteModal();
          treeGrid.current.refresh();
          dispatch(getContractDetails({ contractId }));
        } else {
          dispatch(
            showToastNotification({
              content: untranslated(res.data.message ?? ""),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      }
    };

    const dateTemplate = (args: MileStoneRow) => {
      if (args.mileStoneDate?.timeStamp == null) {
        return "-";
      }
      return convertDate(Number(args.mileStoneDate?.timeStamp), "dateForTooltip");
    };
    const completionDateTemplate = (args: MileStoneRow) => {
      if (args.completionDate == null) {
        return "-";
      }
      return convertDate(Number(args.completionDate), "dateForTooltip");
    };

    const columnDefinitions = useRef([
      {
        id: "title",
        name: t("Title"),
        field: "title",
        minWidth: 150,
        filterType: "Text",
        formatter: Formatters.tree,
      },
      {
        id: "description",
        name: t("Description"),
        field: "description",
        minWidth: 120,
        filterType: "Text",
      },
      {
        id: "mileStoneDate",
        name: t("Milestone Date"),
        field: "mileStoneDate",
        minWidth: 180,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) => dateTemplate(args),
      },
      {
        id: "completionDate",
        name: t("Completion Date"),
        field: "completionDate",
        minWidth: 180,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) => completionDateTemplate(args),
      },
      {
        id: "status",
        name: t("Status"),
        field: "status",
        minWidth: 180,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          mileStonStatusTemplate(args.status, true),
      },
      {
        id: "mileStoneAmount",
        name: `${t("Amount")}`,
        columnGroup: "Milestone",
        field: "mileStoneAmount",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.mileStoneAmount),
      },
      {
        id: "mileStonePercentageOfTotal",
        name: `${t("% Of Total")}`,
        field: "mileStonePercentageOfTotal",
        columnGroup: "Milestone",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.mileStonePercentageOfTotal),
      },
      {
        id: "changeOrderAmount",
        name: `${t("Amount")}`,
        field: "changeOrderAmount",
        columnGroup: "Change Order",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.changeOrderAmount),
      },
      {
        id: "changeOrderPercentageOfTotal",
        name: `${t("% Of Total")}`,
        field: "changeOrderPercentageOfTotal",
        columnGroup: "Change Order",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.changeOrderPercentageOfTotal),
      },
      {
        id: "revisedAmount",
        name: `${t("Amount")}`,
        field: "revisedAmount",
        minWidth: 100,
        columnGroup: "Revised",
        formatter: (data: number, value: number, a: any, b: unknown, args: any) => roundOffTemplate(args.revisedAmount),
      },
      {
        id: "revisedPercentageOfTotal",
        name: `${t("% Of Total")}`,
        field: "revisedPercentageOfTotal",
        minWidth: 100,
        columnGroup: "Revised",
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.revisedPercentageOfTotal),
      },
      {
        id: "usedAmount",
        name: `${t("Amount")}`,
        field: "usedAmount",
        minWidth: 100,
        columnGroup: "Used",
        formatter: (data: number, value: number, a: any, b: unknown, args: any) => roundOffTemplate(args.usedAmount),
      },
      {
        id: "usedPercentageOfTotal",
        name: `${t("% Of Total")}`,
        field: "usedPercentageOfTotal",
        minWidth: 100,
        columnGroup: "Used",
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.usedPercentageOfTotal),
      },
      {
        id: "remainingAmount",
        name: `${t("Amount")}`,
        field: "remainingAmount",
        columnGroup: "Remaining Amount",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.remainingAmount),
      },
      {
        id: "remainingPercentageOfTotal",
        name: `${t("% Of Total")}`,
        field: "remainingPercentageOfTotal",
        columnGroup: "Remaining Amount",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.remainingPercentageOfTotal),
      },
      {
        id: "inspectedAmount",
        name: `${t("Amount")}`,
        field: "inspectedAmount",
        columnGroup: "Inspected",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.inspectedAmount),
      },
      {
        id: "inspectedPercentageOfTotal",
        name: `${t("% Of Total")}`,
        field: "inspectedPercentageOfTotal",
        columnGroup: "Inspected",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.inspectedPercentageOfTotal),
      },
      {
        id: "billedAmount",
        name: `${t("Amount")}`,
        field: "billedAmount",
        columnGroup: "Billed",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) => roundOffTemplate(args.billedAmount),
      },
      {
        id: "billedPercentageOfTotal",
        name: `${t("% Of Total")}`,
        field: "billedPercentageOfTotal",
        columnGroup: "Billed",
        minWidth: 100,
        formatter: (data: number, value: number, a: any, b: unknown, args: any) =>
          roundOffTemplate(args.billedPercentageOfTotal),
      },
    ]);

    const beforeDataBound = (data: any) => {
      const disabledItemIds = data?.data?.result
        ?.filter((item: MileStoneRow) => item.isAlreadyAddedInThisForm)
        .map((item: MileStoneRow) => item.mileStoneId);
      setDisabledRows(disabledItemIds);
      return data.data.result;
    };

    const getGridComponent = () => {
      return (
        <SlickGrid
          ref={treeGrid}
          columnDefinitions={
            // SlickGrid column typing is de-scoped (branded ValidatedColumnDefinition); cast to the prop's type.
            columnDefinitions.current as unknown as ComponentProps<typeof SlickGrid>["columnDefinitions"]
          }
          treeGrid
          enableWrapText
          disabledIds={disabledRows}
          disableCustomSorting
          treeColIndex={1}
          gridOptions={{
            showPreHeaderPanel: true,
            preHeaderPanelHeight: 30,
            topHeaderPanelHeight: 30,
            frozenColumn: 5,
            enableAutoResize: true,
            showHeaderRow: false,
            enableSorting: true,
          }}
          allowPagination={false}
          idMapping="mileStoneId"
          hasChildMapping="hasChild"
          parentIdMapping="parentId"
          initialSortColumnId="createdDate"
          onSelectedRowIdsChanged={selectionHandler}
          toolbar={(showOnlyGrid ? null : toolBarItems) as unknown as ComponentProps<typeof SlickGrid>["toolbar"]}
          toolbarClickHandler={toolbarClickHandler}
          remoteDataSource={mileStoneServiceUrl}
          query={mileStoneQuery}
          beforeDataBound={beforeDataBound}
          enableFiltering={true}
          enableFilteringOption
          enableHeaderGrouping={true}
        />
      );
    };
    const deleteModalFooter = () => (
      <div className="flex gap-4">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={deleteMileStone}>
          {t("Delete")}
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={showHideDeleteModal}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );
    const deleteModalHeader = () => (
      <div className="w-full flex justify-between rounded-md">
        <div className="flex  flex-col gap-1">{t("Delete Milestones(s)")}</div>
      </div>
    );
    const deleteModalContent = () => (
      <div className="flex  flex-col gap-10">
        <span>{t("All Child Milestones will also be deleted. Are you sure you want to continue")}?</span>
      </div>
    );

    const addModalHeader = () => (
      <div className="w-full flex items-center justify-between">
        <span className="flex flex-col ">
          <span>{editMode ? t("Edit Milestone") : t("Create Milestone")}</span>
          {showAddMileStoneModal.isChildContract && (
            <span className="text-12">
              {t(
                "By adding a nested milestone, the Amount & % fields of the parent milestone will get overridden based on the sum of child values.",
              )}
            </span>
          )}
        </span>
      </div>
    );
    const addModalContent = () => {
      let amountContent = t("Available amount : {{amount}}", {
        amount: contractDetails.remainingBudgetAmountForMileStones,
      });
      let percentContent = t("Available % of total : {{percent}}", {
        percent: contractDetails.budgetAmount
          ? ((contractDetails.remainingBudgetAmountForMileStones! * 100) / contractDetails.budgetAmount).toFixed(2)
          : 0,
      });
      if (editMode && treeGrid.current.dataView.getAllSelectedItems()[0]?.hasChild) {
        amountContent = t("Parent milestone's amount cannot be directly changed");
        percentContent = t("Parent milestone's % of total cannot be directly changed");
      }
      return (
        <div className=" flex flex-col gap-5">
          {mileStoneParametersMapper.map((data, index) => (
            <div key={index} className="flex w-full">
              <span className="flex-1 font-semibold">
                <span className="flex gap-5">
                  {data.heading}
                  {(data.id == "amount" || data.id == "%OfTotal") && (
                    <span style={{ width: "fit-content" }}>
                      <IconComponent
                        name="details"
                        width="1.5rem"
                        height="1.5rem"
                        position="bottom"
                        toolTipContent={data.id == "amount" ? amountContent : percentContent}
                      />
                    </span>
                  )}
                </span>
              </span>
              <span className="flex-2">{data.component}</span>
            </div>
          ))}
        </div>
      );
    };
    const addModalFooter = () => {
      let isDisabled = false;
      const { title, description, mileStoneAmount, mileStoneDate } = mileStoneData;
      if (
        title == "" ||
        description == "" ||
        mileStoneAmount == "" ||
        !getContractCRUDPermission(contractDetails, changeOrderState) ||
        mileStoneDate!.timeStamp == null ||
        mileStoneDate!.timeStamp == undefined
      ) {
        isDisabled = true;
      }
      return (
        <div className="">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            disabled={isDisabled}
            onClick={createMileStoneClick}
          >
            {editMode ? t("Save") : t("Create")}
          </ButtonComponent>
        </div>
      );
    };

    const closeTaskListModal = () => {
      setShowTaskList((prev) => ({
        ...prev,
        status: false,
        selectedTask: null,
      }));
    };

    const handleTaskSelect = () => {
      const selectedTask = taskGridRef.current.dataView.getAllSelectedItems();
      if (selectedTask.length) {
        let date = selectedTask[0].startDate;
        if (mileStoneData.dateType === "endDate") {
          date = selectedTask[0].endDate;
        }
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            ...prev.mileStoneDate,
            timeStamp: date,
            selectedDate: date,
            taskId: selectedTask[0]._id,
            taskTitle: selectedTask[0].title,
            offSet: 0,
          },
        }));

        setShowTaskList({ status: false, selectedTask: selectedTask[0] });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select task first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    };

    const getTaskList = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={
          (contractDetails.linkedSchedule?._id && (
            <div className="">
              <ButtonComponent onClick={handleTaskSelect} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
                {t("Select")}
              </ButtonComponent>
            </div>
          )) ||
          undefined
        }
        width="70%"
        height={"80%"}
        headerTitle={<div className="flex w-full justify-between items-center">{t("Select Activity")}</div>}
        onCancel={closeTaskListModal}
        isVisible={showTaskList.status}
        modelContent={<ContractTasks allowCheckBoxSelection={false} ref={taskGridRef} contractId={contractId} />}
      />
    );

    return (
      <>
        {showAddMileStoneModal.status && (
          <ModalStateDialogComponent
            footerContent={addModalFooter()}
            width="50%"
            allowModalPadding={false}
            headerTitle={addModalHeader()}
            onCancel={handleAddMileStoneModalShow}
            isVisible
            modelContent={addModalContent()}
          />
        )}
        {showDeleteModal && (
          <ModalStateDialogComponent
            footerContent={deleteModalFooter()}
            width="30%"
            headerTitle={deleteModalHeader()}
            onCancel={showHideDeleteModal}
            isVisible
            modelContent={deleteModalContent()}
          />
        )}
        <div className="flex overflow-auto w-full h-full flex-col">
          {isModuleDisabled ? <DisabledIllustration /> : getGridComponent()}
        </div>
        {showTaskList.status && getTaskList()}
      </>
    );
  },
);
MileStones.displayName = "MileStones";
export default MileStones;
